// The SyncEngine: the station's local SQLite plus its sync connection (D-019). One interface,
// these implementations:
//   PowerSyncEngine over @powersync/web        web build, Playwright, LAN tablets in a browser
//   PowerSyncEngine over @powersync/capacitor  Android/iPadOS (beta, proven on tablets later)
//   PowerSyncEngine over @powersync/node       Electron main process (./powersync/node.ts)
//   IpcSyncEngine                              Electron renderer → that engine, over IPC
//   FakeSyncEngine (./testing)                 unit and integration tests (in-memory SQLite)
//
// Reads are SQL over the local schema (./schema.ts). Writes happen in a write transaction; a
// station action is a command appended to the outbox in the same transaction as its local
// effect (plan §9.3), see `enqueueCommand` and ./outbox.ts.

import type { CommandEnvelope } from '@enp/domain';

/** A value bound to a `?` placeholder. */
export type SqlValue = string | number | null;

/** Read access to the local database (outside or inside a transaction). */
export interface LocalReader {
  getAll<T>(sql: string, params?: readonly SqlValue[]): Promise<T[]>;
  getOptional<T>(sql: string, params?: readonly SqlValue[]): Promise<T | null>;
}

/** A write transaction: everything in it commits together or not at all. */
export interface LocalTx extends LocalReader {
  execute(sql: string, params?: readonly SqlValue[]): Promise<{ rowsAffected: number }>;
}

/** Credentials for the sync service (a device JWT, plan §10.2). */
export interface SyncCredentials {
  /** The sync service URL. */
  endpoint: string;
  token: string;
  expiresAt?: Date;
}

/** One queued local change. Outbox rows are `PUT`s on `outbox_command`. */
export interface UploadOp {
  /** The row id (`command_id` for outbox rows). */
  id: string;
  table: string;
  op: 'PUT' | 'PATCH' | 'DELETE';
  data: Record<string, unknown> | undefined;
}

/** The oldest queued changes, in order. `complete()` removes exactly these from the queue. */
export interface UploadBatch {
  ops: UploadOp[];
  /** More changes are queued behind this batch. */
  haveMore: boolean;
  complete(): Promise<void>;
}

/** What the engine calls to authenticate and to upload (the app's side of sync). */
export interface SyncConnector {
  /** Credentials for the sync service, or null when there are none (not paired). */
  fetchCredentials(): Promise<SyncCredentials | null>;
  /**
   * Upload queued changes. Complete a batch only once the server has answered every
   * command in it with a final answer; throw to keep it queued (the engine retries later).
   */
  uploadData(engine: SyncEngine): Promise<void>;
}

export interface ConnectOptions {
  /**
   * Open the download stream from the sync service. Off when no sync service is configured
   * (commands still upload to the API).
   */
  stream?: boolean;
}

/**
 * A sync stream the station asks for with parameters (the others are auto-subscribed),
 * e.g. `{ stream: 'production_done', params: { day: '2026-09-29' } }` (B-049).
 */
export interface StreamSubscription {
  stream: string;
  params: Record<string, string>;
}

/** The stream of the branch's batches finished on one business day (`{ day }`, B-049). */
export const PRODUCTION_DONE_STREAM = 'production_done';

/** The stream of the branch's deliveries received on one business day (`{ day }`, B-054). */
export const RECEIVING_DONE_STREAM = 'receiving_done';

/** The stream of the branch's orders promised on one business day (`{ day }`, WP7.3). */
export const ORDERS_HANDOVER_STREAM = 'orders_handover';

/** One key per stream and parameters (parameter order doesn't matter). */
export function streamKey(s: StreamSubscription): string {
  const params = Object.keys(s.params)
    .sort()
    .map((k) => [k, s.params[k]]);
  return `${s.stream}|${JSON.stringify(params)}`;
}

/** Why the last attempt failed. Never terminal: the engine keeps retrying. */
export interface SyncProblem {
  kind: 'open' | 'download' | 'upload';
  message: string;
  /** No answer from the server (the device may still be online): "can't reach the server". */
  network?: boolean;
}

/** What the engine is doing (plan §9.3: the station always shows it). */
export interface SyncEngineStatus {
  /** The local database is open. */
  ready: boolean;
  /** The download stream is connected. */
  connected: boolean;
  connecting: boolean;
  /** Last complete download. */
  lastSyncedAt: Date | undefined;
  downloading: boolean;
  uploading: boolean;
  /** Local changes waiting to upload (every queued change, commands and their effects). */
  pendingCount: number;
  /** Commands the server keeps for a manager (D-024), while the hold is open. */
  heldCount: number;
  /** Commands the server refused for good, not yet acknowledged at the station. */
  rejectedCount: number;
  error: SyncProblem | undefined;
}

export const INITIAL_ENGINE_STATUS: Readonly<SyncEngineStatus> = Object.freeze({
  ready: false,
  connected: false,
  connecting: false,
  lastSyncedAt: undefined,
  downloading: false,
  uploading: false,
  pendingCount: 0,
  heldCount: 0,
  rejectedCount: 0,
  error: undefined,
});

/** A command as the caller gives it: everything except `device_seq`, which the outbox assigns. */
export type CommandDraft = Omit<CommandEnvelope, 'device_seq'>;

/**
 * A draft made inside the command's transaction, e.g. to take a document number
 * (`nextDocNumber`) in the same transaction as the command that carries it (plan §9.4).
 */
export type CommandDraftInTx = (tx: LocalTx) => Promise<CommandDraft>;

/** Runs inside the command's transaction, after the outbox row is written. */
export type LocalEffect = (tx: LocalTx, envelope: CommandEnvelope) => Promise<void>;

export type EngineKind =
  'powersync-web' | 'powersync-capacitor' | 'powersync-node' | 'electron-ipc' | 'fake';

export interface SyncEngine extends LocalReader {
  readonly kind: EngineKind;

  /** Opens the local database (idempotent). */
  open(): Promise<void>;
  /** Disconnects and closes the local database. */
  close(): Promise<void>;

  /** Starts syncing: downloads (when `stream`) and uploads through `connector`. */
  connect(connector: SyncConnector, options?: ConnectOptions): Promise<void>;
  disconnect(): Promise<void>;

  /**
   * The parameterised streams to download, replacing the previous set: new ones are
   * subscribed, ones no longer listed are let go (their rows leave once the service drops
   * them). Kept across reconnects; works before `connect` too.
   */
  setStreamSubscriptions(subscriptions: readonly StreamSubscription[]): Promise<void>;

  getStatus(): SyncEngineStatus;
  /** Calls `listener` on every status change. Returns the unsubscribe function. */
  subscribe(listener: (status: SyncEngineStatus) => void): () => void;

  /** Runs `sql` now and again whenever the tables it reads change. Returns a stop function. */
  watch<T>(
    sql: string,
    params: readonly SqlValue[],
    onResult: (rows: T[]) => void,
    onError?: (error: Error) => void,
  ): () => void;

  writeTransaction<T>(fn: (tx: LocalTx) => Promise<T>): Promise<T>;

  /**
   * Appends a command to the outbox and runs its local effect, in **one** local transaction:
   * both happen or neither. Assigns the next `device_seq`. Validates the command against the
   * shared registry first (an invalid command is never queued). Returns the full envelope.
   */
  enqueueCommand(
    draft: CommandDraft | CommandDraftInTx,
    localEffect?: LocalEffect,
  ): Promise<CommandEnvelope>;

  /** The oldest queued changes (at most `limit`), or null when the queue is empty. */
  nextUploadBatch(limit: number): Promise<UploadBatch | null>;

  /**
   * How many commands wait in the upload queue (other queued local changes not counted),
   * without reading the queue itself (review CR8B-14).
   */
  queuedCommandCount(): Promise<number>;

  /**
   * Tries to upload now (when connected to a connector) and resolves when the attempt ends.
   * Failures land in `status.error`; they never reject.
   */
  uploadNow(): Promise<void>;

  /**
   * Disconnects and deletes every local row: synced data, the upload queue (unsent commands
   * are lost) and the local-only tables. For wiping a station before it joins another
   * account (review CR3-7). The database stays open.
   */
  clearLocalData(): Promise<void>;
}
